export function formatCurrency(
  value: number | null,
  precision = 0,
  options?: {
    currency?: string;
    currencySign?: "standard" | "accounting";
    notation?: "standard" | "scientific" | "engineering" | "compact";
    compactDisplay?: "short" | "long";
  },
) {
  if (value == null || Number.isNaN(value)) return "—";
  // Round to the displayed precision first so tiny negative dust
  // (e.g. -1e-9 from Decimal payloads) renders as $0, not "-$0".
  const rounding = 10 ** precision;
  value = Math.round(value * rounding) / rounding;
  if (Object.is(value, -0)) value = 0;
  const { currency, currencySign, notation, compactDisplay } = {
    currency: "USD",
    ...options,
  };
  return value.toLocaleString(undefined, {
    maximumFractionDigits: precision,
    style: "currency",
    currency,
    currencySign,
    notation,
    compactDisplay,
  });
}

export function formatCurrencyComponent(
  value: number,
  precision = 0,
  options?: {
    currency?: string;
    currencySign?: "standard" | "accounting";
    notation?: "standard" | "scientific" | "engineering" | "compact";
    compactDisplay?: "short" | "long";
  },
) {
  if (value == null || Number.isNaN(value)) return "—";
  const stringValue = formatCurrency(value, precision, {
    ...options,
    currencySign: options?.currencySign ?? "accounting",
  });

  return value >= 0 ? (
    stringValue
  ) : (
    <span className="text-danger">{stringValue}</span>
  );
}

export function formatPercent(value: number | null, precision = 0) {
  if (value == null || Number.isNaN(value)) return "—";
  // Round to the displayed precision first so tiny negative dust
  // (e.g. -7e-11 from Decimal payloads) normalizes to 0, not "-0%".
  const factor = 10 ** (precision + 2);
  value = Math.round(value * factor) / factor;
  if (Object.is(value, -0)) value = 0;
  return value.toLocaleString(undefined, {
    maximumFractionDigits: precision,
    style: "percent",
  });
}

/**
 * A model metric percent (0 to 1) as `formatPercent` shows it, or "N/A" when
 * there is no value: FastTrack sent no number for it, or the model's funds do
 * not have the history the metric needs.
 */
export function formatMetricPercent(
  value: number | null | undefined,
  precision = 2,
) {
  if (value == null || Number.isNaN(value)) return "N/A";
  return formatPercent(value, precision);
}

export function formatDecimal(value: number, precision = 4) {
  return value.toFixed(precision);
}

// Coerce a possibly-stringified numeric value (python Decimal serialised as a
// string e.g. "893493.00" / "0E-21", an already-numeric value, or
// null/undefined) to a number, defaulting to 0 for missing/non-numeric input.
// Shared so the various explain/rebalance views don't each re-implement the
// `s == null ? 0 : Number(s)` pattern.
export function parseNumeric(
  value: string | number | null | undefined,
): number {
  const parsed =
    typeof value === "number" ? value : Number.parseFloat(value ?? "");
  return Number.isFinite(parsed) ? parsed : 0;
}
